import { modelData } from 'app/lib/modelData'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { graphql } from 'app/generated/gql'
import { Disclosure } from '@headlessui/react'
import { UserCircleIcon } from '@heroicons/react/24/solid'
import classNames from 'classnames'
import { Crown, PhoneIncome as PhoneIncomeIcon } from 'iconoir-react'
import moment from 'moment'
import { Fragment, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import srtParser2 from 'srt-parser-2'
import { useOutboundCall } from '../click-to-call/OutboundCallProvider'
import { PostCallModal } from '../click-to-call/PostCallModal'
import { StaffAssign } from './StaffAssign'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { ActivityTags } from 'app/components/activities/ActivityTags'
import { AiSummary } from 'app/components/activities/AiSummary'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import AudioPlayer from 'app/components/AudioPlayer'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { FeedDivider } from 'app/components/shared/styled/FeedDivider'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useQuery } from 'urql'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import {
  findActiveTranscriptLineIndex,
  formatSeconds,
  formatSpeaker,
  getSpeakerColor,
  processTranscriptWithTransfers,
  SPEAKER_PREFIX_PATTERN,
  stripSpeakerPrefix,
  updateTranscriptHighlight,
} from 'app/utils/transcript'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { sortTags } from 'lib/sortTags'
import { useCallStore } from 'app/stores/StoreContext'
import { REASSIGN_ACTIVITY_MUTATION } from 'app/stores/ActivityStore'
import { gqlMutation } from 'app/lib/graphqlClient'
import { gqlFetchOne } from 'app/lib/graphqlClient'

const OUTBOUND_CALL_QUERY = graphql(`
  query OutboundCall($id: ID!) {
    call(id: $id) {
      ai {
        actionItems
        answerer
        channel
        extractionDate
        model
        people {
          appointmentDate
          appointmentRequested
          appointmentScheduled
          appointmentTime
          birthDate
          email
          index
          insuranceProvider
          isCaller
          name
          outcomes
          patientType
          phone
          reasonForNotScheduling
          referral
          relationship
          serviceRequested
          treatment
        }
        summary
        tokenUsage {
          inputTokens
          outputTokens
          totalTokens
        }
      }
      aiAgent
      allPatientOutcomes
      answered
      answerer
      appointmentDateString
      archived
      callerId
      callerPhone
      channel
      createdDate
      direction
      duration
      gclid
      golden
      id
      landingPageUrl
      locationId
      note
      patientNames
      recordingDuration
      recordings
      referringUrl
      staff {
        id
        name
      }
      status
      tags
      trackingNumber
      trackingNumberName
      treatment
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      waveforms
      workspaceId
    }
  }
`)

const CALL_RECORDING_URL_QUERY = graphql(`
  query OutboundCallRecordingUrl($id: ID!) {
    callRecordingUrl(id: $id)
  }
`)

const CALL_TRANSCRIPTION_QUERY = graphql(`
  query OutboundCallTranscription($id: ID!) {
    callTranscription(id: $id)
  }
`)

type TabNames = `Call Details` | `Transcript` | `Post Processing`

type Props = {
  activity: ActivityJson
  contact?: ContactJson
  currentAudioPlaying?: number | string
  isFirst?: boolean
  isLast?: boolean
  /** See `ActivityCard`. */
  hideHeader?: boolean
  singleActivity?: boolean
  onAudioPlay?: (id: number | string) => void
  onActivityUpdated?: (activity: ActivityJson) => void
  onContactRefresh?: () => void
}

/**
 * Renders outbound Call activity record.
 */
export function OutboundCallCard({
  activity: _activity,
  contact,
  currentAudioPlaying,
  isFirst,
  isLast,
  hideHeader,
  singleActivity,
  onAudioPlay,
  onActivityUpdated,
  onContactRefresh,
}: Props): ReactElement {
  const me = useUser()
  const [confirm] = useConfirmation()
  const { activeCall } = useOutboundCall()
  const callStore = useCallStore()
  const fetchJson = async (url: string, opts?: RequestInit) =>
    window
      .fetch(url, { credentials: `include`, headers: { 'Content-Type': `application/json` }, ...opts })
      .then((r) => r.json())
  const [localActivity, setLocalActivity] = useState<ActivityJson>(_activity)
  const [staff, setStaff] = useState<ActivityJson[`owner`]>(localActivity.owner)
  const [selectedTab, setSelectedTab] = useState<TabNames>(`Call Details`)
  const [seekTo, setSeekTo] = useState<number | undefined>(undefined)
  const transcriptionsRef = useRef<HTMLDivElement>(null)
  const [deleting, setDeleting] = useState(false)
  const localActivityRef = useRef(localActivity)
  localActivityRef.current = localActivity
  const callRef = useRef<CallJson | undefined>(undefined)

  const isActivityOlderThanTwoYears = useMemo(
    () => moment().diff(moment(localActivity.activityDate), `months`) > 24,
    [localActivity],
  )

  const queryContext = useMemo(() => ({ suppressErrorToast: true }), [])

  // Fetch call data and transcription
  const [{ data: callResponse, error: responseError, fetching: callFetching }, reexecuteQuery] = useQuery({
    query: OUTBOUND_CALL_QUERY,
    variables: { id: _activity.typeId! },
    pause: !_activity.typeId,
    context: queryContext,
  })
  const reloadCall = () => reexecuteQuery({ requestPolicy: `network-only` })

  const [{ data: recordingData }] = useQuery({
    query: CALL_RECORDING_URL_QUERY,
    variables: { id: _activity.typeId! },
    pause: !_activity.typeId || isActivityOlderThanTwoYears,
    context: queryContext,
  })

  const [{ data: transcriptionData }] = useQuery({
    query: CALL_TRANSCRIPTION_QUERY,
    variables: { id: _activity.typeId! },
    pause: !_activity.typeId,
  })

  const isLoading = useMemo(() => callFetching && !callResponse?.call, [callFetching, callResponse])
  const call: CallJson | undefined = useMemo(() => callResponse?.call as CallJson | undefined, [callResponse])
  callRef.current = call
  const ai: CallJson[`ai`] = call?.ai

  var parser = new srtParser2()
  const transcription = useMemo(
    () => (transcriptionData?.callTranscription ? parser.fromSrt(transcriptionData.callTranscription) : undefined),
    [transcriptionData],
  )
  const processedTranscript = useMemo(
    () => (transcription ? processTranscriptWithTransfers(transcription) : undefined),
    [transcription],
  )

  const diarized = useMemo(
    () => processedTranscript?.lines.some((line) => SPEAKER_PREFIX_PATTERN.test(line.text)) ?? false,
    [processedTranscript],
  )

  const onStaffChange = async (staff: ActivityJson[`owner`]) => {
    setStaff(staff)

    // API expects flat ownerId/ownerName fields, not a nested owner object
    const ownerId = staff?.id ?? `-1`
    const ownerName = staff?.name ?? `-`
    const result = await gqlMutation(REASSIGN_ACTIVITY_MUTATION, {
      id: localActivity.id!,
      input: { ownerId, ownerName },
    })
    if (result.error) {
      logError(`Failed to update staff`, result.error)
      notifyError(`Failed to update staff`)
      setStaff(localActivity.owner)
      return
    }
    const updated = result.data?.reassignActivity
      ? { ...modelData(result.data.reassignActivity), data: localActivity.data }
      : undefined
    // Only call activity update if it comes from ActivityDrawer
    singleActivity && updated && onActivityUpdated?.(updated)
    notifySuccess(`Staff updated successfully`)
  }

  async function handleDelete() {
    if (!call?.id) return
    await confirm({
      title: `Delete Call`,
      description: `Are you sure you want to delete this call?`,
      variant: `danger`,
    }).then(async () => {
      setDeleting(true)
      const result = await callStore.remove(call!.id!)
      if (!result.error) {
        notifySuccess(`Call deleted`)
        onContactRefresh?.()
      } else {
        notifyError(`Failed to delete call`)
      }
      setDeleting(false)
    })
  }

  function showEditCallModal() {
    showModal(
      <PostCallModal
        call={call!}
        contact={contact!}
        activity={localActivity}
        onSubmit={(updatedActivity) => {
          if (updatedActivity) {
            setLocalActivity(updatedActivity)
          }
          reloadCall()
          closeModal()
        }}
        onContactRefresh={onContactRefresh}
      />,
      { title: `Outbound Call`, isFullScreen: false, removePadding: true, useRadix: true },
      false,
    )
  }

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Icon = () => {
    const iconProps = { className: `h-5 w-5 rounded-md bg-blue-600 text-on-color p-1` }
    return <PhoneIncomeIcon {...iconProps} style={{ transform: `rotate(180deg)` }} />
  }

  const activityTags = () => {
    const tags = localActivity.tags || []
    let displayTags = sortTags(tags)

    // Show status tags for active outbound call (UI-only, not persisted)
    const isActiveCall = call?.id && activeCall?.callId === call.id
    if (isActiveCall && activeCall?.status === `success`) {
      displayTags = [`Completed`, ...displayTags.filter((t) => t !== `Processing`)]
    } else if (!call?.recordingDuration) {
      // Check if call had duration but recording not yet available (still processing)
      if (call?.duration && call.duration > 0) {
        displayTags = displayTags.filter((t) => t !== `No Recording` && t !== `Processing`)
        displayTags = [...displayTags, `Processing Audio`]
      }
      // } else if (call?.answered === false) {
      //   // Office didn't answer - TRK-4496
      //   displayTags = [...displayTags, `Office Didn't Answer`]
      // }
    }

    // Agent preview (test) calls are archived but visible to super users.
    // "Archived" is injected here (not persisted as a tag) so super users can tell it's a test call.
    if (localActivity?.archived) {
      displayTags = [`Archived`, ...displayTags]
    }

    return <ActivityTags activityTags={displayTags} workspaceId={localActivity.workspaceId} isLoading={isLoading} />
  }

  function handlePlayerTimeUpdate(currentTime: number) {
    if (!processedTranscript) return
    const activeLineIndex = findActiveTranscriptLineIndex(
      processedTranscript.lines,
      currentTime,
      processedTranscript.transferIndex,
      processedTranscript.timeOffset,
    )
    updateTranscriptHighlight(transcriptionsRef, activeLineIndex)
  }

  // Poll for activity/call updates for 5 minutes after outbound call creation
  useEffect(() => {
    const currentCall = callResponse?.call
    if (currentCall?.direction !== `outbound` || !currentCall?.createdDate) return

    const stopAt = moment(currentCall.createdDate).add(5, `minutes`)
    if (moment().isAfter(stopAt)) return

    const pollForUpdates = async () => {
      try {
        const currentActivity = localActivityRef.current
        const activityResult = await gqlFetchOne<ActivityJson>(
          'activity',
          currentActivity.id!,
          'id locationId type typeId activityDate needsReview tags',
        )
        const callResult = currentActivity.typeId
          ? await gqlFetchOne<{ recordings?: string[] }>('call', currentActivity.typeId, 'id recordings')
          : undefined
        if (!activityResult) return

        const activityChanged =
          currentActivity.activityDate !== activityResult.activityDate ||
          currentActivity.needsReview !== activityResult.needsReview ||
          JSON.stringify(currentActivity.tags) !== JSON.stringify(activityResult.tags)
        const recordingsAppeared = callResult?.recordings?.length && !callRef.current?.recordings?.length

        if (activityChanged) {
          setLocalActivity({ ...currentActivity, ...activityResult })
        }
        if (activityChanged || recordingsAppeared) {
          reloadCall()
        }
        // oxlint-disable-next-line local/require-catch-logging
      } catch (error) {
        debug(`call-polling`, `Error polling for activity updates:`, error)
      }
    }

    const intervalId = setInterval(() => {
      if (moment().isAfter(stopAt) || callRef.current?.recordings?.length) {
        clearInterval(intervalId)
        return
      }
      bg(pollForUpdates())
    }, 5000)

    bg(pollForUpdates())

    return () => clearInterval(intervalId)
  }, [call?.createdDate, call?.direction, localActivity.id])

  if (!me) return <></>

  // The API answers null when the call has no audio in S3 (TRK-5885). A call still tagged
  // "Processing Audio" is not empty yet, so it keeps the idle player.
  const hasNoRecording = recordingData?.callRecordingUrl === null && !(call?.duration && !call.recordingDuration)

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Tabs = () => {
    return (
      <div className="flex gap-x-6 pt-3" aria-label="Tabs">
        {(
          [
            `Call Details`,
            transcription ? `Transcript` : undefined,
            me.type === `super` && ai ? `Post Processing` : undefined,
          ] as TabNames[]
        )
          .filter((tab) => tab !== undefined)
          .map((tab) => (
            <button
              key={tab}
              className={classNames(
                tab === selectedTab
                  ? `text-strong border-blue-500`
                  : `border-transparent text-ui-xs text-subtle hover:border-transparent hover:text-subtle`,
                `flex items-center gap-x-2 whitespace-nowrap border-b-[2px] pb-2.5 text-ui-xs`,
              )}
              onClick={() => setSelectedTab(tab as TabNames)}
            >
              {tab === `Post Processing` && (
                <div className="size-5 flex items-center justify-center rounded bg-amber-500/[0.08]">
                  <Crown width={12} height={12} className="text-amber-800" />
                </div>
              )}
              <span>{tab}</span>
            </button>
          ))}
      </div>
    )
  }

  return (
    <ActivityCard
      hideHeader={hideHeader}
      icon={<Icon />}
      title={<span className="text-ui-sm text-strong">Outbound Call</span>}
      activityDate={localActivity?.activityDate}
      tags={activityTags()}
      content={
        <div id="activity-record-call" className="block w-full px-4">
          {hasNoRecording ? (
            <p className="text-ui-xs text-subtle">No recording is available for this call.</p>
          ) : (
            <AudioPlayer
              key={_activity.id}
              id={localActivity.id!}
              callId={call?.id}
              className="p-0"
              imageHeight="h-7"
              currentAudioPlaying={currentAudioPlaying}
              waveforms={call?.waveforms}
              url={isActivityOlderThanTwoYears ? undefined : (recordingData?.callRecordingUrl ?? undefined)}
              urlNotFoundMessage={
                isActivityOlderThanTwoYears
                  ? `There was an error loading this call's audio. It may be too old.`
                  : undefined
              }
              onPlay={(id) => {
                onAudioPlay?.(id!)
              }}
              seekTo={seekTo}
              onTimeUpdate={handlePlayerTimeUpdate}
            />
          )}
        </div>
      }
      drawer={
        responseError ? (
          // See `InboundCallCard`: a failed fetch drew the same empty shell as a
          // call that genuinely had nothing on it, so an outage read as an answer.
          <div className="mt-3 flex items-center justify-between gap-3 border-y border-default px-4 py-3">
            <span className="text-body-xs text-subtle">This call could not be loaded.</span>
            <Button
              label="Try again"
              mode="white"
              size="sm"
              type="button"
              className="h-6 !px-3 !text-ui-xs"
              onClick={reloadCall}
            />
          </div>
        ) : (
          <>
            <div className="mt-3 flex justify-between border-y border-default px-4">
              <Tabs />
            </div>
            <div className="px-4 pb-1 pt-4">
              {selectedTab === `Call Details` ? (
                <>
                  <KeyValueDrawer
                    map={{
                      Staff: (
                        <StaffAssign
                          staff={staff}
                          workspaceId={call?.workspaceId}
                          locationId={call?.locationId}
                          onChange={(staff) => onStaffChange(staff)}
                        />
                      ),
                      // For outbound calls: Caller = staff's businessPhone, Contact = patient's callerPhone
                      // For inbound calls: Caller = caller's phone, Contact = tracking number
                      "Caller's Number":
                        formatPhoneNumber(call?.direction === `outbound` ? call?.businessPhone : call?.callerPhone) ||
                        `-`,
                      "Contact's Number":
                        formatPhoneNumber(call?.direction === `outbound` ? call?.callerPhone : call?.trackingNumber) ||
                        `-`,
                      'Connected?': localActivity?.tags?.includes(`Connected`)
                        ? `Yes`
                        : localActivity?.tags?.includes(`Not Connected`)
                          ? `No`
                          : `-`,
                      'Scheduled?': localActivity?.tags?.includes(`Appointment Scheduled`)
                        ? `Yes`
                        : localActivity?.tags?.includes(`Appointment Not Scheduled`)
                          ? `No`
                          : `-`,
                      'Appointment Date': call?.appointmentDateString
                        ? formatDate({ value: call.appointmentDateString })
                        : `-`,
                      Notes: call?.note || `-`,
                    }}
                    className={{
                      "Caller's Number": `phi`,
                      "Contact's Number": `phi`,
                      Notes: `phi`,
                    }}
                    omitLabels={[`Staff`]}
                  />
                  {me.type === `super` && (
                    <div className="mt-3 flex w-full flex-col text-body-xs text-strong">
                      <span className="text-subtle">Call ID</span>
                      <div className="mt-0.5 flex items-center gap-x-1 text-ui-xs text-strong">
                        {call?.id || `-`}
                        <CopyToClipboard text={call?.id || `-`} />
                      </div>
                    </div>
                  )}
                  <div className="flex w-full justify-end gap-2">
                    {/* Show delete only when call was not connected */}
                    {!call?.tags?.includes(`Connected`) && (
                      <Button
                        label="Delete"
                        mode="danger"
                        size="sm"
                        type="button"
                        onClick={handleDelete}
                        loading={deleting}
                        disabled={!call}
                      />
                    )}
                    <Button
                      label="Edit Call"
                      mode="tertiary"
                      size="sm"
                      type="button"
                      onClick={showEditCallModal}
                      disabled={!call || !contact}
                    />
                  </div>
                </>
              ) : selectedTab === `Transcript` && processedTranscript ? (
                <Disclosure as="div" className="bg-surface-subtle">
                  <Disclosure.Panel ref={transcriptionsRef} className="flex flex-col overflow-hidden bg-surface" static>
                    {processedTranscript.lines.map((line, i) => (
                      <Fragment key={i}>
                        {line.textBeforeTransfer && (
                          <div
                            data-line-index={i}
                            className={classNames(`flex cursor-pointer px-4 py-2`)}
                            onClick={() => setSeekTo(line.adjustedStart)}
                          >
                            {diarized && (
                              <UserCircleIcon
                                className="h-6 w-6 shrink-0"
                                color={getSpeakerColor(line.textBeforeTransfer)}
                              />
                            )}
                            <div className={diarized ? `ml-3` : undefined}>
                              <div className="flex items-center">
                                {diarized && (
                                  <p className="text-heading-xs leading-5 text-strong">
                                    {formatSpeaker(line.textBeforeTransfer)}
                                  </p>
                                )}
                                <p className={classNames(`text-caption-md text-subtle`, diarized && `ml-2`)}>
                                  {formatSeconds(line.adjustedStart)}
                                </p>
                              </div>
                              <p className="mt-2 text-body-sm text-strong">
                                {stripSpeakerPrefix(line.textBeforeTransfer)}
                              </p>
                            </div>
                          </div>
                        )}
                        {line.isTransferLine ? (
                          <FeedDivider text="Transferred" className="my-1.5" />
                        ) : (
                          <div
                            data-line-index={i}
                            className={classNames(`flex cursor-pointer px-4 py-2`)}
                            onClick={() => setSeekTo(line.adjustedStart)}
                          >
                            {diarized && (
                              <UserCircleIcon className="h-6 w-6 shrink-0" color={getSpeakerColor(line.text)} />
                            )}
                            <div className={diarized ? `ml-3` : undefined}>
                              <div className="flex items-center">
                                {diarized && (
                                  <p className="text-heading-xs leading-5 text-strong">{formatSpeaker(line.text)}</p>
                                )}
                                <p className={classNames(`text-caption-md text-subtle`, diarized && `ml-2`)}>
                                  {formatSeconds(line.adjustedStart)}
                                </p>
                              </div>
                              <p className="mt-2 text-body-sm text-strong">{stripSpeakerPrefix(line.text)}</p>
                            </div>
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </Disclosure.Panel>
                </Disclosure>
              ) : selectedTab === `Post Processing` && ai ? (
                <AiSummary ai={ai} activity={localActivity} />
              ) : null}
            </div>
          </>
        )
      }
      alwaysOpen={true}
      isFirst={isFirst}
      isLast={isLast}
      singleActivity={singleActivity}
    />
  )
}
